/**
 * ASH 샘플 → **세션 타임라인**.
 *
 * "이 세션이 언제 무엇을 하고 있었나" 를 가로 막대로 보이게 하는 계산이다.
 * 화면에서 떼어 둔 이유: 여기서 틀리면 그림이 그럴듯하게 거짓말하는데, 그건 눈으로
 * 알아챌 수가 없다. 규칙을 표로 검증할 수 있어야 한다.
 *
 * **ASH 의 성질을 그대로 지킨다**:
 *   - 1초에 한 번, **활성 세션만** 찍힌다. 그래서 **샘플이 없는 구간 = 비활성**이고,
 *     그것은 "대기 0" 이 아니라 **잰 적이 없다**는 뜻이다(`gap`). 0 으로 메우면
 *     "쉬지 않고 돌았다" 로 읽힌다.
 *   - `event` 가 비면 **ON CPU** 다(이 저장소 관례 — `session_state` 를 쓰지 않는다).
 *   - 한 실행의 경계는 `(sql_id, sql_exec_id)` 다. `sql_id` 만으로 묶으면 같은 SQL 의
 *     다른 실행이 한 덩어리로 붙는다.
 *   - 병렬 실행은 **세션이 여럿**이다(QC + PX 워커). 한 줄에 겹쳐 그리면 시간이
 *     중복돼 보이므로 세션마다 줄을 따로 둔다.
 */

/** ASH 기본 샘플 주기(초) */
const DEFAULT_INTERVAL_SEC = 1;
/** 이 배수를 넘게 벌어지면 그 사이는 "잰 적 없음" 으로 본다 */
const GAP_FACTOR = 1.5;

function toMs(v) {
    if (v == null) return NaN;
    if (typeof v === 'number') return v;
    const ms = new Date(v).getTime();
    return Number.isFinite(ms) ? ms : NaN;
}

/**
 * 표본의 갈래 — 커밋·롤백(서버가 is_commit 으로 가른다 — 드라이버의 commit() 호출 · COMMIT 문장 · log file sync)이 먼저,
 * 그다음 이벤트가 비어 있으면 CPU 를 쓰고 있던 것이다(2026-10-22 — 배치가 커밋에 쓴 시간을 SQL 과 따로 보려고)
 */
function kindOf(sample) {
    if (sample && sample.isCommit) return 'commit';
    return sample && sample.event ? 'wait' : 'cpu';
}

/** 같은 막대로 이어 붙일 수 있는가 — 하나라도 다르면 경계를 긋는다 */
function sameSegment(a, b) {
    return a.kind === b.kind
        && (a.event || null) === (b.event || null)
        && (a.sqlId || null) === (b.sqlId || null)
        && String(a.sqlExecId ?? '') === String(b.sqlExecId ?? '')
        // 서브프로그램이 바뀌면 SQL 이 같아도 나눈다 — 안 그러면 PL/SQL 경계가 뭉개진다.
        // **entry 도 본다**: entry 가 바뀌면 그건 아예 다른 최상위 호출이다
        // (단위 테스트가 이 빠짐을 잡았다 — inner 만 보면 두 호출이 한 막대로 붙었다).
        && (a.plsqlKey || null) === (b.plsqlKey || null)
        && (a.plsqlEntryKey || null) === (b.plsqlEntryKey || null);
}

/** 줄(lane) 키 — 인스턴스 + 세션. 병렬이면 워커마다 다른 줄이 된다. */
function laneKeyOf(s) {
    return `${s.instId ?? 0}:${s.sessionId ?? 0}:${s.sessionSerial ?? 0}`;
}

/**
 * @param {Array} samples ASH 행 — {sampleTime, instId, sessionId, sessionSerial, sqlId,
 *   sqlExecId, event, waitClass, xid, qcSessionId, pxServerNumber, program}
 * @param {object} [opt]
 * @param {number} [opt.intervalSec]
 * @returns {{lanes: Array, from: number, to: number, intervalMs: number}}
 */
function buildTimeline(samples, { intervalSec = DEFAULT_INTERVAL_SEC } = {}) {
    const intervalMs = Math.max(1, Number(intervalSec) || DEFAULT_INTERVAL_SEC) * 1000;
    const rows = (Array.isArray(samples) ? samples : [])
        .map(s => ({ ...s, _ms: toMs(s.sampleTime) }))
        .filter(s => Number.isFinite(s._ms))
        .sort((a, b) => a._ms - b._ms);

    if (rows.length === 0) return { lanes: [], from: null, to: null, intervalMs };

    const from = rows[0]._ms;
    const to = rows[rows.length - 1]._ms + intervalMs;

    const byLane = new Map();
    rows.forEach((s) => {
        const key = laneKeyOf(s);
        if (!byLane.has(key)) {
            byLane.set(key, {
                key,
                instId: s.instId ?? null,
                sessionId: s.sessionId ?? null,
                sessionSerial: s.sessionSerial ?? null,
                qcSessionId: s.qcSessionId ?? null,
                // QC 인지 워커인지 — 병렬에서 어느 줄이 조정자인지 보여야 읽힌다.
                //
                // **워커 번호로 판정하지 않는다.** ASH 에는 워커 번호 컬럼이 없고(px_server# 는
                // 다른 뷰의 것이다) PROGRAM 에서 읽는 값이라 못 읽을 수 있다 — 그러면 워커가
                // 전부 조정자로 보인다. 판정은 `qc_session_id` 로 한다:
                // 값이 없거나 자기 자신이면 조정자, 다른 세션을 가리키면 워커다.
                isQc: s.qcSessionId == null || s.qcSessionId === s.sessionId,
                pxServerNumber: s.pxServerNumber ?? null,
                // 워커 이름(P00F·PPA7) — 실제 운영의 이름은 영숫자라 번호보다 이것이 먼저다
                pxServerName: s.pxServerName || null,
                qcSessionSerial: s.qcSessionSerial ?? null,
                qcInstId: s.qcInstId ?? null,
                program: s.program || null,
                segments: [],
                transactions: [],
                _samples: [],
            });
        }
        byLane.get(key)._samples.push(s);
    });

    for (const lane of byLane.values()) {
        const segs = [];
        let prevEnd = null;
        lane._samples.forEach((s) => {
            const start = s._ms;
            const end = s._ms + intervalMs;
            // **샘플이 비어 있던 구간은 채우지 않는다.** 잰 적이 없다는 사실 자체를 막대로 남긴다.
            if (prevEnd != null && start - prevEnd > intervalMs * (GAP_FACTOR - 1)) {
                segs.push({ kind: 'gap', from: prevEnd, to: start, samples: 0, event: null, waitClass: null, sqlId: null, sqlExecId: null });
            }
            const cand = {
                kind: kindOf(s),
                event: s.event || null,
                waitClass: s.waitClass || null,
                sqlId: s.sqlId || null,
                sqlExecId: s.sqlExecId ?? null,
                from: start,
                to: end,
                samples: 1,
                xid: s.xid || null,
                // PL/SQL 귀속 — 이름이 없으면 id 로라도 구간을 가른다(이름은 못 찾을 수 있다)
                plsqlEntryKey: s.plsqlEntryObjectId != null
                    ? `${s.plsqlEntryObjectId}:${s.plsqlEntrySubprogramId ?? 0}` : null,
                plsqlEntryName: s.plsqlEntryName || null,
                plsqlKey: s.plsqlObjectId != null
                    ? `${s.plsqlObjectId}:${s.plsqlSubprogramId ?? 0}` : null,
                plsqlName: s.plsqlName || null,
            };
            const last = segs[segs.length - 1];
            if (last && last.kind !== 'gap' && sameSegment(last, cand) && last.to >= start) {
                last.to = end;
                last.samples += 1;
            } else {
                segs.push(cand);
            }
            prevEnd = end;
        });
        lane.segments = segs;

        // 트랜잭션 — ASH 의 xid 가 보이는 구간이 그 트랜잭션이 살아 있던 구간이다.
        // **시작/종료를 정확히 안다고 말하지 않는다**: 샘플에 잡힌 처음과 마지막일 뿐이고,
        // 세션이 비활성인 동안에도 트랜잭션은 열려 있을 수 있다.
        const byXid = new Map();
        lane._samples.forEach((s) => {
            if (!s.xid) return;
            const cur = byXid.get(s.xid);
            if (!cur) byXid.set(s.xid, { xid: s.xid, from: s._ms, to: s._ms + intervalMs, samples: 1 });
            else { cur.to = s._ms + intervalMs; cur.samples += 1; }
        });
        lane.transactions = [...byXid.values()];
        delete lane._samples;
    }

    // QC 를 맨 위에, 그다음 워커 번호 순 — 병렬 실행을 위에서 아래로 읽는다.
    // 워커 번호를 못 읽었으면 세션 번호로 세운다(순서를 지어내지 않되, 조회마다 뒤바뀌지도 않게).
    const lanes = [...byLane.values()].sort((a, b) => {
        if (a.isQc !== b.isQc) return a.isQc ? -1 : 1;
        if ((a.instId ?? 0) !== (b.instId ?? 0)) return (a.instId ?? 0) - (b.instId ?? 0);
        // 이름이 있으면 이름으로 — P001 < P00F < PPA7 처럼 글자 순서가 곧 오라클이 붙인 순서다
        if (a.pxServerName && b.pxServerName && a.pxServerName !== b.pxServerName) {
            return a.pxServerName < b.pxServerName ? -1 : 1;
        }
        const an = a.pxServerNumber, bn = b.pxServerNumber;
        if (an != null && bn != null && an !== bn) return an - bn;
        if (an != null && bn == null) return -1;
        if (an == null && bn != null) return 1;
        return (a.sessionId ?? 0) - (b.sessionId ?? 0);
    });

    return { lanes, from, to, intervalMs };
}

/**
 * **PL/SQL 구간** — 프로시저 호출을 막대로 본다.
 *
 * `which` 로 두 가지 질문에 답한다:
 *   - `'entry'`: **최상위로 들어간 프로시저**. 그 호출이 끝날 때까지 값이 고정되므로
 *     이것이 곧 「프로시저 실행 구간」이다 — 사용자가 물은 그 경계.
 *   - `'inner'`: **지금 실행 중인 서브프로그램**. 중첩 호출마다 바뀐다.
 *
 * 값이 없는 구간(PL/SQL 밖)은 **구간을 만들지 않는다** — 빈 자리가 곧 "SQL 만 돌았다" 는 뜻이다.
 *
 * **한계**: ASH 는 1초 표본이라 짧은 호출은 아예 안 잡힌다. 그래서 이 구간은
 * "몇 번 호출됐나" 를 말해 주지 못하고 "어디서 오래 걸렸나" 만 답한다.
 */
function plsqlSpans(lane, which = 'entry') {
    const idKey = which === 'entry' ? 'plsqlEntryKey' : 'plsqlKey';
    const nameKey = which === 'entry' ? 'plsqlEntryName' : 'plsqlName';
    const out = [];
    (lane?.segments || []).forEach((seg) => {
        const id = seg[idKey];
        if (seg.kind === 'gap' || !id) return;
        const last = out[out.length - 1];
        if (last && last.id === id && last.to >= seg.from) {
            last.to = seg.to;
        } else {
            out.push({ id, name: seg[nameKey] || null, from: seg.from, to: seg.to });
        }
    });
    return out;
}

/**
 * 실행(SQL) 경계 — 막대 위에 구분선을 긋기 위한 구간 목록.
 * `(sql_id, sql_exec_id)` 가 바뀌는 곳이 경계다.
 */
function executionSpans(lane) {
    const out = [];
    (lane?.segments || []).forEach((seg) => {
        if (seg.kind === 'gap' || !seg.sqlId) return;
        const last = out[out.length - 1];
        if (last && last.sqlId === seg.sqlId && String(last.sqlExecId ?? '') === String(seg.sqlExecId ?? '') && last.to >= seg.from) {
            last.to = seg.to;
        } else {
            out.push({ sqlId: seg.sqlId, sqlExecId: seg.sqlExecId ?? null, from: seg.from, to: seg.to });
        }
    });
    return out;
}

/** 막대 색 — 대기 클래스별 색은 화면이 이미 쓰는 것을 그대로 쓴다(여기서는 갈래만 정한다) */
function segmentKindLabelKey(seg) {
    if (!seg) return null;
    if (seg.kind === 'gap') return 'inactive';
    if (seg.kind === 'cpu') return 'onCpu';
    if (seg.kind === 'commit') return 'commit';
    return 'wait';
}

/** 요약 — 막대 위에 "CPU 몇 초 / 대기 몇 초" 를 적기 위한 값 */
function summarize(lane, intervalMs = 1000) {
    const out = { cpuMs: 0, waitMs: 0, commitMs: 0, gapMs: 0 };
    (lane?.segments || []).forEach((seg) => {
        const d = Math.max(0, seg.to - seg.from);
        if (seg.kind === 'cpu') out.cpuMs += d;
        else if (seg.kind === 'wait') out.waitMs += d;
        else if (seg.kind === 'commit') out.commitMs += d;
        else out.gapMs += d;
    });
    out.intervalMs = intervalMs;
    return out;
}

/**
 * 보이는 구간의 **SQL 별 합계**(2026-10-22 — 배치 세션은 실행 수백 번이 한 줄에 겹쳐 막대만으로는 읽을 수 없었다).
 * 표본 시간을 [from, to] 로 잘라 SQL_ID 마다 더한다. 실행 수는 그 구간에 보인 SQL_EXEC_ID 의 개수다 —
 * 1초 표본이라 짧은 실행은 빠지므로 「적어도 이만큼」 이다. share 는 SQL 이 없는 표본까지 포함한 활성 시간 대비.
 */
function sqlBreakdown(lanes, from, to) {
    const bySql = new Map();
    let activeMs = 0;
    (lanes || []).forEach((lane) => (lane.segments || []).forEach((seg) => {
        if (seg.kind === 'gap') return;
        const a = Math.max(seg.from, from);
        const b = Math.min(seg.to, to);
        if (!(b > a)) return;
        const d = b - a;
        activeMs += d;
        if (!seg.sqlId) return;
        let r = bySql.get(seg.sqlId);
        if (!r) { r = { sqlId: seg.sqlId, ms: 0, cpuMs: 0, waitMs: 0, execs: new Set(), events: new Map() }; bySql.set(seg.sqlId, r); }
        r.ms += d;
        if (seg.kind === 'cpu') r.cpuMs += d;
        else {
            r.waitMs += d;
            if (seg.event) r.events.set(seg.event, (r.events.get(seg.event) || 0) + d);
        }
        if (seg.sqlExecId != null) r.execs.add(String(seg.sqlExecId));
    }));
    return [...bySql.values()].map((r) => {
        let topEvent = null; let topMs = 0;
        r.events.forEach((ms, ev) => { if (ms > topMs) { topMs = ms; topEvent = ev; } });
        return {
            sqlId: r.sqlId, ms: r.ms, cpuMs: r.cpuMs, waitMs: r.waitMs,
            execCount: r.execs.size, topEvent, share: activeMs > 0 ? r.ms / activeMs : 0,
        };
    }).sort((x, y) => y.ms - x.ms || (x.sqlId < y.sqlId ? -1 : 1));
}

/**
 * **소요 시간 구성**(2026-10-22) — 「1시간 중 30분은 sql1, 10분은 sql2 …」 를 막대로 보이기 위한 값.
 *
 * - 세션 하나(`pool: false`): 분모는 **벽시계 시간**(보는 구간의 길이). 병렬이면 **조정자 줄만** 센다 — 워커까지 더하면
 *   벽시계를 넘는다(조정자는 워커를 기다리는 동안에도 그 SQL 의 표본이 찍힌다). 표본이 없는 시간은 「비활성」 이다 —
 *   세션 풀이면 풀에서 쉬던 시간, 아니면 앱 처리·네트워크 왕복이다.
 * - 세션 풀(`pool: true`): 여러 세션이 **동시에** 돌아 벽시계로 나누면 100% 를 넘는다. 분모는 **DB 시간**(모든 세션 표본의 합)이고
 *   비활성 막대는 없다. 대신 평균 동시 세션(DB 시간 ÷ 벽시계)을 준다.
 * 갈래: SQL 마다 · 커밋(kind commit — SQL 과 따로) · SQL 없음(PL/SQL 연산 등) · 기타(상위 topN 밖의 SQL) · 비활성.
 * 시간은 1초 표본 수로 어림한 값이다.
 */
function timeComposition(lanes, from, to, { pool = false, topN = 6 } = {}) {
    const span = Math.max(0, (to ?? 0) - (from ?? 0));
    const all = lanes || [];
    const qcLanes = all.filter(l => l.isQc);
    const use = pool ? all : (qcLanes.length ? qcLanes : all);
    const buckets = new Map();
    const add = (key, kind, sqlId, seg, d) => {
        let b = buckets.get(key);
        if (!b) { b = { key, kind, sqlId: sqlId || null, ms: 0, cpuMs: 0, waitMs: 0 }; buckets.set(key, b); }
        b.ms += d;
        if (seg.kind === 'cpu') b.cpuMs += d; else b.waitMs += d;
    };
    let activeMs = 0;
    use.forEach((lane) => (lane.segments || []).forEach((seg) => {
        if (seg.kind === 'gap') return;
        const a = Math.max(seg.from, from);
        const b = Math.min(seg.to, to);
        if (!(b > a)) return;
        const d = b - a;
        activeMs += d;
        if (seg.kind === 'commit') add('commit', 'commit', null, seg, d);
        else if (!seg.sqlId) add('nosql', 'nosql', null, seg, d);
        else add(`sql:${seg.sqlId}`, 'sql', seg.sqlId, seg, d);
    }));
    const base = pool ? activeMs : Math.max(span, activeMs);
    const sorted = [...buckets.values()].sort((x, y) => y.ms - x.ms || (x.key < y.key ? -1 : 1));
    // 상위 topN 개의 SQL 만 따로 — 커밋·SQL 없음은 늘 따로 둔다(배치에서 커밋이 큰지가 바로 보여야 한다)
    const rows = [];
    const others = { key: 'others', kind: 'others', sqlId: null, ms: 0, cpuMs: 0, waitMs: 0, count: 0 };
    let sqlShown = 0;
    sorted.forEach((b) => {
        if (b.kind === 'sql' && sqlShown >= topN) {
            others.ms += b.ms; others.cpuMs += b.cpuMs; others.waitMs += b.waitMs; others.count += 1;
            return;
        }
        if (b.kind === 'sql') sqlShown += 1;
        rows.push(b);
    });
    if (others.count > 0) rows.push(others);
    if (!pool) {
        const idle = Math.max(0, span - activeMs);
        if (idle > 0) rows.push({ key: 'idle', kind: 'idle', sqlId: null, ms: idle, cpuMs: 0, waitMs: 0 });
    }
    rows.forEach((r) => { r.share = base > 0 ? r.ms / base : 0; });
    return {
        span, activeMs, base, pool, rows,
        sessions: use.length,
        avgActive: span > 0 ? activeMs / span : 0,
    };
}

export {
    timeComposition,
    sqlBreakdown,
    buildTimeline,
    plsqlSpans,
    executionSpans,
    summarize,
    segmentKindLabelKey,
    kindOf,
    DEFAULT_INTERVAL_SEC,
};
